import React, { useEffect, useRef, useState } from 'react';
import { FiTrash2, FiUserPlus, FiX } from 'react-icons/fi';
import { useAuth } from '../auth/AuthContext';
import {
  inviteProjectMember,
  listProjectMembers,
  removeProjectMember,
  updateProjectMemberRole,
} from '../utils/conversationApi';
import { KpfAvatar, KpfSuggestionList, useKpfSuggestions } from './KpfEmailSuggestions';
import './ProjectMembersModal.css';

const CLOSE_ANIMATION_MS = 220;
const KPF_EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@kpf\.com$/i;

const ROLE_LABELS = { owner: 'Owner', edit: 'Edit', view: 'View' };

// People are shown by name; the email only when no name is known (e.g. an
// invite not yet accepted). Inviting is still by email, in the box below.
const memberLabel = (m, fallback) => m.display_name || m.email || fallback;

/**
 * "Manage members" (creator) / "See who has access" (everyone else) for a
 * project -- see .claude/plan/projectsharing.md's "Permission model" and
 * Frontend work section. Deliberately a modal opened from ProjectView's
 * "..." menu, not a 4th tab, matching how Rename/Delete project already
 * live there.
 *
 * Owner is structural (the project's creator, never assignable via invite
 * or role-change), so `isCreator` -- not a role string -- decides whose row
 * is untouchable. Inviting, changing access and removing people are open to
 * Edit members too (`canManage`). `createdBy`/`myRole` are a UI courtesy only; every
 * mutating action below is re-checked server-side by require_project_role
 * regardless, so this can never be the actual security boundary.
 */
export default function ProjectMembersModal({
  isOpen, onClose, projectId, projectName, createdBy, myRole, ownerless = false, onLeaveRequested,
}) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [roleInput, setRoleInput] = useState('view');
  const [inviting, setInviting] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  // The member whose trash icon was clicked: removing someone (or cancelling
  // an invite) waits for confirmation in a small dialog over this one.
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmError, setConfirmError] = useState('');
  const suggest = useKpfSuggestions();
  const inputWrapRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setIsClosing(false);
    setError('');
    setEmailInput('');
    setConfirmRemove(null);
    setConfirmError('');
    suggest.reset();
    let cancelled = false;
    setLoading(true);
    listProjectMembers(projectId)
      .then((list) => { if (!cancelled) setMembers(list); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load members.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // suggest.reset is a stable callback; the effect is about opening/closing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, projectId]);

  // Escape backs out of the confirmation only, leaving the members list open.
  useEffect(() => {
    if (!confirmRemove) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !busyKey) setConfirmRemove(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmRemove, busyKey]);

  if (!isOpen) return null;

  // Falls back to the old role-string heuristic only if createdBy wasn't
  // supplied (defensive -- every ProjectResponse has carried created_by
  // since the View/Edit rename, so this should never actually matter).
  const myMember = members.find((m) => m.user_id && user && m.user_id === user.id);
  const isCreator = createdBy
    ? Boolean(user && user.id === createdBy)
    : (myMember ? myMember.role === 'owner' : true);
  const canManage = isCreator || (myRole || myMember?.role) === 'edit';

  // Stays open while an invite, access change or removal is running, like the
  // Leave and invitation dialogs: closing first would hide a failure, and in
  // App.js (one dialog for every project) a result landing after it reopened
  // on another project would be added to that project's list.
  const handleClose = () => {
    if (isClosing || inviting || busyKey) return;
    setIsClosing(true);
    setTimeout(onClose, CLOSE_ANIMATION_MS);
  };

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);
    suggest.search(value);
  };

  const handleSelectSuggestion = (candidate) => {
    setEmailInput(candidate.email);
    suggest.reset();
    setError('');
  };

  const handleInvite = async () => {
    const trimmed = emailInput.trim();
    if (!trimmed) {
      setError('Enter an email address.');
      return;
    }
    if (!KPF_EMAIL_REGEX.test(trimmed)) {
      setError('Only @kpf.com addresses can be invited.');
      return;
    }
    setError('');
    setInviting(true);
    try {
      const newMember = await inviteProjectMember(projectId, trimmed, roleInput);
      setMembers((prev) => [...prev, newMember]);
      setEmailInput('');
    } catch (err) {
      setError(err.message || 'Failed to send invite.');
    } finally {
      setInviting(false);
    }
  };

  const rowKey = (m) => m.user_id || m.email;

  const handleRoleChange = async (member, newRole) => {
    setError('');
    setBusyKey(rowKey(member));
    try {
      // Same addressing as handleRemove below: a pending invite has no real
      // user_id yet, so it's changed through its PENDING#<email> entry.
      const idForUpdate = member.user_id || `PENDING#${member.email}`;
      const updated = await updateProjectMemberRole(projectId, idForUpdate, newRole);
      setMembers((prev) => prev.map((m) => (rowKey(m) === rowKey(member) ? updated : m)));
    } catch (err) {
      setError(err.message || "Failed to update this member's access.");
    } finally {
      setBusyKey(null);
    }
  };

  const askRemove = (member) => {
    setError('');
    setConfirmError('');
    setConfirmRemove(member);
  };

  const closeConfirm = () => {
    if (busyKey) return;
    setConfirmRemove(null);
  };

  const handleRemove = async (member) => {
    setConfirmError('');
    const key = rowKey(member);
    setBusyKey(key);
    try {
      // A still-pending invite has no real user_id yet -- the backend
      // stores it as PENDING#<email>, which is what the delete route
      // needs, not the (absent) real id.
      const idForRemoval = member.user_id || `PENDING#${member.email}`;
      await removeProjectMember(projectId, idForRemoval);
      setMembers((prev) => prev.filter((m) => rowKey(m) !== key));
      setConfirmRemove(null);
    } catch (err) {
      setConfirmError(err.message || (member.pending
        ? 'Failed to cancel this invite.'
        : 'Failed to remove this member.'));
    } finally {
      setBusyKey(null);
    }
  };

  // Leaving is a decision with consequences for the member's own
  // conversations, so it has its own dialog (LeaveProjectModal, owned by the
  // panel) rather than a one-click button here: this popup just hands over.
  const handleLeave = () => {
    if (inviting || busyKey) return;
    onClose();
    if (onLeaveRequested) onLeaveRequested();
  };

  return (
    <div
      className={`pmm-overlay ${isClosing ? 'pmm-overlay--closing' : ''}`}
      onClick={handleClose}
    >
      <div
        className={`pmm-modal ${isClosing ? 'pmm-modal--closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pmm-header">
          <h2>{canManage ? 'Manage members' : 'See who has access'}</h2>
          <button
            type="button"
            className="pmm-close"
            onClick={handleClose}
            disabled={inviting || Boolean(busyKey)}
            aria-label="Close"
          >
            <FiX size={20} />
          </button>
        </div>

        <div className="pmm-body">
          {projectName && <p className="pmm-subtitle">"{projectName}"</p>}
          {ownerless && (
            <p className="pmm-note">
              The owner has left this project. Anyone with Edit access can still invite people,
              change access and remove people.
            </p>
          )}

          {loading ? (
            <p className="pmm-loading">Loading members...</p>
          ) : (
            <ul className="pmm-list">
              {members.map((m) => {
                const key = rowKey(m);
                const isMe = Boolean(user && m.user_id && m.user_id === user.id);
                // The creator's row is identified the same way as the
                // viewer's own -- by matching user_id against createdBy --
                // not by the stored role string, for the same structural
                // reason isCreator is computed that way above.
                const isRowCreator = Boolean(createdBy && m.user_id === createdBy) || m.role === 'owner';
                const isRowBusy = busyKey === key;
                // An Edit member gets no controls on their own row: one slip
                // to View would take away their own access to manage, and
                // removing yourself is what Leave project is for.
                const canChangeRow = canManage && !isRowCreator && !(isMe && !isCreator);
                return (
                  <li key={key} className="pmm-row">
                    <div className="pmm-info">
                      <KpfAvatar candidate={m} />
                      <span className="pmm-name">
                        {/* Never a bare user_id, an internal identifier
                            shown to no one, including the owner's own row. */}
                        {memberLabel(m, 'Unknown member')}
                        {isMe && <span className="pmm-you"> (you)</span>}
                      </span>
                      {m.pending && (
                        <span
                          className="pmm-pending-badge"
                          title="Invited -- hasn't accepted yet, so has no access to this project"
                        >
                          Pending
                        </span>
                      )}
                    </div>
                    {canChangeRow ? (
                      <div className="pmm-actions">
                        <select
                          className="pmm-role-select"
                          value={m.role}
                          disabled={isRowBusy}
                          onChange={(e) => handleRoleChange(m, e.target.value)}
                        >
                          <option value="view">View</option>
                          <option value="edit">Edit</option>
                        </select>
                        <button
                          type="button"
                          className="pmm-remove-btn"
                          onClick={() => askRemove(m)}
                          disabled={isRowBusy}
                          title={m.pending ? 'Cancel invite' : 'Remove from project'}
                          aria-label={`${m.pending ? 'Cancel invite for' : 'Remove'} ${memberLabel(m, 'this member')}`}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    ) : (
                      <span className="pmm-role-label">
                        {ROLE_LABELS[m.role] || 'View'}
                      </span>
                    )}
                  </li>
                );
              })}
              {!loading && members.length === 0 && (
                <li className="pmm-empty">No members yet.</li>
              )}
            </ul>
          )}

          {canManage && (
            <div className="pmm-invite">
              <label className="pmm-label">Invite by email (KPF only)</label>
              <div className="pmm-invite-row">
                <div className="pmm-invite-input-wrap" ref={inputWrapRef}>
                  {/* type="text", not "email": this box takes a name too, and
                      an email input drops a trailing space from its value,
                      so a full name types unreliably. autoFocus: opening
                      Manage members is usually to invite someone, so the
                      cursor starts here, ready to type. */}
                  <input
                    autoFocus
                    type="text"
                    inputMode="email"
                    className="pmm-invite-input"
                    value={emailInput}
                    onChange={handleEmailInputChange}
                    onFocus={suggest.reopen}
                    onBlur={suggest.close}
                    placeholder="Name or email"
                    disabled={inviting}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={suggest.open}
                    aria-controls={suggest.listId}
                    aria-activedescendant={suggest.activeOptionId}
                    aria-autocomplete="list"
                    onKeyDown={(e) => {
                      // Arrow keys / Enter-on-a-highlighted-row / Escape belong
                      // to the suggestion list while it is open.
                      if (suggest.handleKeyDown(e, handleSelectSuggestion)) return;
                      if (e.key === 'Enter') handleInvite();
                    }}
                  />
                  {suggest.open && (
                    <KpfSuggestionList
                      anchorRef={inputWrapRef}
                      suggestions={suggest.suggestions}
                      activeIndex={suggest.activeIndex}
                      onHover={suggest.setActiveIndex}
                      onSelect={handleSelectSuggestion}
                      listId={suggest.listId}
                    />
                  )}
                </div>
                <select
                  className="pmm-role-select"
                  value={roleInput}
                  onChange={(e) => setRoleInput(e.target.value)}
                  disabled={inviting}
                >
                  <option value="view">View</option>
                  <option value="edit">Edit</option>
                </select>
                <button
                  type="button"
                  className="pmm-invite-btn"
                  onClick={handleInvite}
                  disabled={inviting}
                >
                  <FiUserPlus size={14} /> {inviting ? 'Inviting...' : 'Invite'}
                </button>
              </div>
            </div>
          )}

          {error && <p className="pmm-error">{error}</p>}
        </div>

        <div className="pmm-footer">
          {/* The creator can't leave their own project -- delete it from
              the project menu instead -- so this action simply isn't
              offered to them, rather than being shown disabled. */}
          {!isCreator && (
            <button
              type="button"
              className="pmm-leave-btn"
              onClick={handleLeave}
              disabled={inviting || Boolean(busyKey)}
            >
              Leave project
            </button>
          )}
          <button type="button" className="pmm-done-btn" onClick={handleClose} disabled={inviting || Boolean(busyKey)}>
            Done
          </button>
        </div>
      </div>

      {/* A sibling of the card, not inside it: the card's transform animation
          would otherwise become the containing block for this fixed overlay.
          Clicks stop here so they never reach the outer overlay's close. */}
      {confirmRemove && (
        <RemoveMemberConfirm
          member={confirmRemove}
          projectName={projectName}
          busy={busyKey === rowKey(confirmRemove)}
          error={confirmError}
          onCancel={closeConfirm}
          onConfirm={() => handleRemove(confirmRemove)}
        />
      )}
    </div>
  );
}

/**
 * "Are you sure?" before removing someone -- or cancelling an invite -- from
 * the members list. The wording follows what remove_project_member actually
 * does: access ends straight away, the person's own conversations are saved
 * to the same "Project-<name>" folder leaving uses (nothing is deleted, and a
 * re-invite offers them back), and knowledge base files they added stay in
 * the project.
 */
function RemoveMemberConfirm({ member, projectName, busy, error, onCancel, onConfirm }) {
  const who = memberLabel(member, 'this member');
  const isPending = Boolean(member.pending);
  const isEdit = member.role === 'edit';
  // Matches the folder name the server uses (_archive_members_conversations).
  const folderName = `Project-${(projectName || 'Untitled').trim()}`.slice(0, 100);

  return (
    <div
      className="pmm-confirm-overlay"
      onClick={(e) => { e.stopPropagation(); onCancel(); }}
    >
      <div
        className="pmm-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="pmm-confirm-title"
        aria-describedby="pmm-confirm-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="pmm-confirm-title" className="pmm-confirm-title">
          {isPending ? 'Cancel this invite?' : 'Remove this person from the project?'}
        </h3>
        <p className="pmm-confirm-who" title={who}>{who}</p>

        {isPending ? (
          <div id="pmm-confirm-desc">
            <p className="pmm-confirm-text">
              They haven&apos;t accepted yet, so they have no access to this project and no work in
              it. You can invite them again at any time.
            </p>
          </div>
        ) : (
          <div id="pmm-confirm-desc">
            <p className="pmm-confirm-text">
              They have <strong>{isEdit ? 'Edit' : 'View'} access</strong>, so they may have{' '}
              {isEdit ? 'added files and conversations to' : 'conversations in'} this project.
              If you remove them:
            </p>
            <ul className="pmm-confirm-list">
              <li>They lose access straight away, including the knowledge base and instructions.</li>
              <li>
                Their own conversations from this project are saved in a <strong>{folderName}</strong>{' '}
                folder in their Conversations. Nothing is deleted.
              </li>
              <li>Any files they added to the knowledge base stay in the project.</li>
            </ul>
            <p className="pmm-confirm-text pmm-confirm-muted">
              To give them access again, someone with Edit access would have to invite them again.
              They can then bring their saved conversations back into the project.
            </p>
          </div>
        )}

        {error && <p className="pmm-error" role="alert">{error}</p>}

        <div className="pmm-confirm-footer">
          <button
            type="button"
            className="pmm-confirm-btn pmm-confirm-btn--ghost"
            onClick={onCancel}
            disabled={busy}
            autoFocus
          >
            {isPending ? 'Keep invite' : 'Cancel'}
          </button>
          <button
            type="button"
            className="pmm-confirm-btn pmm-confirm-btn--danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {isPending
              ? (busy ? 'Cancelling...' : 'Cancel invite')
              : (busy ? 'Removing...' : 'Remove')}
          </button>
        </div>
      </div>
    </div>
  );
}
